import React, { useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography
} from "@mui/material";
import {
	Settlement,
	SETTLEMENT_STATUS_LABEL,
	SettlementStatusEnum
} from "../../../store/services/affiliate/dto/affiliate.dto";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatDate } from "../../../utils/formatDate";

const SettlementModal = ({
	open,
	onClose,
	settlement,
	status,
	onStatusChange
}: {
	open: boolean;
	onClose: () => void;
	settlement: Settlement;
	status: SettlementStatusEnum;
	onStatusChange: (status: SettlementStatusEnum) => void;
}) => {
	const [localStatus, setLocalStatus] = useState<SettlementStatusEnum>(status);

	const handleChange = (event: SelectChangeEvent<SettlementStatusEnum>) => {
		setLocalStatus(Number(event.target.value) as SettlementStatusEnum);
	};

	const handleSave = () => {
		onStatusChange(localStatus);
		onClose();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth>
			<DialogTitle>Settlement Details</DialogTitle>
			<DialogContent>
				<Box display={"flex"} flexDirection={"column"} gap={2}>
					<Box>
						<Typography variant={"caption"} color={"textSecondary"}>
							Affiliate
						</Typography>
						<Typography fontWeight={600}>
							{settlement.firstName} {settlement.lastName}
						</Typography>
					</Box>
					<Box>
						<Typography variant={"caption"} color={"textSecondary"}>
							Business Name
						</Typography>
						<Typography fontWeight={600}>{settlement.businessName}</Typography>
					</Box>
					{settlement.payoutWalletNumber && (
						<Box>
							<Typography variant={"caption"} color={"textSecondary"}>
								Wallet Phone Number
							</Typography>
							<Typography fontWeight={600}>{settlement.payoutWalletNumber}</Typography>
						</Box>
					)}
					{settlement.payoutBankAccountNumber && (
						<Box display={"flex"} flexDirection={"column"} gap={2}>
							<Box>
								<Typography variant={"caption"} color={"textSecondary"}>
									Bank Name
								</Typography>
								<Typography fontWeight={600}>{settlement.payoutBankName}</Typography>
							</Box>
							<Box>
								<Typography variant={"caption"} color={"textSecondary"}>
									Bank Account number
								</Typography>
								<Typography fontWeight={600}>{settlement.payoutBankAccountNumber}</Typography>
							</Box>
						</Box>
					)}
					<Box display={"flex"} justifyContent={"space-between"}>
						<Box>
							<Typography variant={"caption"} color={"textSecondary"}>
								Total Sales
							</Typography>
							<Typography fontWeight={600}>{formatCurrency(settlement.totalSales, "EGP")}</Typography>
						</Box>
						<Box textAlign={"right"}>
							<Typography variant={"caption"} color={"textSecondary"}>
								Net Amount to Settle
							</Typography>
							<Typography fontWeight={700} color={"primary.dark"}>
								{formatCurrency(settlement.netSales, "EGP")}
							</Typography>
						</Box>
					</Box>
					{settlement.paidAt && (
						<Box>
							<Typography variant={"caption"} color={"textSecondary"}>
								Paid At
							</Typography>
							<Typography fontWeight={600}>{formatDate(settlement.paidAt, "Africa/Cairo", "PP")}</Typography>
						</Box>
					)}
					<FormControl fullWidth size={"small"}>
						<InputLabel id={"settlement-status-label"}>Settlement Status</InputLabel>
						<Select
							labelId={"settlement-status-label"}
							value={localStatus}
							label={"Settlement Status"}
							onChange={handleChange}
						>
							{(Object.keys(SETTLEMENT_STATUS_LABEL).map(Number) as SettlementStatusEnum[]).map(key => (
								<MenuItem key={key} value={key}>
									{SETTLEMENT_STATUS_LABEL[key]}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
				<Button variant={"contained"} onClick={handleSave} disabled={localStatus === status}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SettlementModal;
